import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
} from "react";
import { getAuth, User, signOut } from "firebase/auth";
import { firebaseApp } from "./firebase";
import { fetchUserDetailsAPI, AuthError, UserDetails } from "./auth";
import useWorkspaces from "./hooks/useWorkspaces";

interface AuthContextType {
  currentUser: UserDetails | null;
  loading: boolean;
  error: AuthError | { code: "unknown" } | null;
  clearError: () => void;
  logout: () => Promise<void>;
  refreshCurrentUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { fetchWorkspaces } = useWorkspaces();
  const [currentUser, setCurrentUser] = useState<UserDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<AuthError | { code: "unknown" } | null>(
    null
  );
  const auth = getAuth(firebaseApp);

  const loadUserAndWorkspaceData = useCallback(async () => {
    try {
      const userDetails = await fetchUserDetailsAPI();
      const workspaces = await fetchWorkspaces();

      let selectedWorkspace = null;
      if (workspaces && workspaces.length > 0) {
        selectedWorkspace = workspaces[0];
      }

      setCurrentUser({ ...userDetails, workspaces, selectedWorkspace });
      setError(null);
    } catch (err: any) {
      console.error("Failed to load user and workspace data:", err);
      setError(err);
      await logout();
    }
  }, [fetchWorkspaces]);

  const refreshCurrentUser = useCallback(async () => {
    setLoading(true);
    await loadUserAndWorkspaceData();
    setLoading(false);
  }, [loadUserAndWorkspaceData]);

  const logout = async () => {
    await signOut(auth);
    setCurrentUser(null);
  };

  const clearError = () => setError(null);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user: User | null) => {
      if (user) {
        await loadUserAndWorkspaceData();
      } else {
        setCurrentUser(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [auth, loadUserAndWorkspaceData]);

  const value = {
    currentUser,
    loading,
    error,
    clearError,
    logout,
    refreshCurrentUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
